import { useMemo } from 'react';

import { FAGroupViewModel, FokParameterChoice, FokParameterInfo } from '@fora-monorepo/employer-my-pages-common/types';
import { getSwedishDateTimeToday, isDateWithinValidityPeriod } from '@fora-monorepo/date-utils';

import { FokParameterForm } from './FokParameterForm';
import { MarkupAndParametersFormProps, OrganizedParameters } from './types';
import { CurrentMarkupInfo } from './CurrentMarkupInfo';
import { StartDateAndMarkupSelect } from './StartDateAndMarkupSelect';
import { useTypedTranslation } from '../../../../src/i18n/i18n';

export const MarkupAndParametersForm = ({ activeMarkup, ...rest }: MarkupAndParametersFormProps) => {
  const { t } = useTypedTranslation();

  const labelText = activeMarkup ? 'Byt uppmärkning från och med:' : t('common.startDate');
  return (
    <>
      {activeMarkup && <CurrentMarkupInfo activeMarkup={activeMarkup} />}

      <MarkupSelectForm activeMarkup={activeMarkup} {...rest} ariaLabel={labelText} />
    </>
  );
};

const MarkupSelectForm = ({
  disabled,
  activeMarkup,
  startDateInputProps,
  startDateInputValue,
  setParameterChoicesInput,
  markupOptions,
  markupSelectInput,
  employmentStartDate,
  ariaLabel,
  formType,
}: MarkupAndParametersFormProps) => {
  const selectedFAGroup = markupOptions.find(({ fokId }) => fokId === markupSelectInput.props.value);

  const organizedParameters = useMemo(() => {
    if (selectedFAGroup) {
      return organizeParameters({
        parameterGroups: selectedFAGroup.fokParameters || [],
        activeParameters: activeMarkup?.fokParameterChoices,
        markupStartDate: startDateInputValue,
      });
    }
    // In edit mode - use has not selected new markup, just display the current parameter options
    const activeFAGroup = markupOptions.find(m => m.fokId === activeMarkup?.fokId);
    return organizeParameters({
      parameterGroups: activeFAGroup?.fokParameters || [],
      activeParameters: activeMarkup?.fokParameterChoices,
      markupStartDate: getSwedishDateTimeToday(),
    });
  }, [activeMarkup, markupOptions, selectedFAGroup, startDateInputValue]);

  return (
    <>
      <StartDateAndMarkupSelect
        startDateInputValue={startDateInputValue}
        startDateInputProps={startDateInputProps}
        markupSelectInput={markupSelectInput}
        markupOptions={markupOptions}
        disabled={disabled}
        ariaLabel={ariaLabel}
      />
      {organizedParameters && (
        <FokParameterForm
          {...organizedParameters}
          setParameterChoicesInput={setParameterChoicesInput}
          disabled={disabled}
          employmentStartDate={employmentStartDate}
          formType={formType}
        />
      )}
    </>
  );
};

/** Find the relevant parameter group for the selected markup based on the group's validity period and markup start date
 *  and organize those parameters into active and available parameters based on the current active parameters and the employee's age */
const organizeParameters = ({
  parameterGroups,
  activeParameters,
  markupStartDate,
}: {
  parameterGroups: FAGroupViewModel['fokParameters'];
  activeParameters: FokParameterChoice[] | undefined;
  markupStartDate: string;
}): OrganizedParameters | null => {
  // Only use the input if it's a full date, to prevent glitching while typing date manually
  const digitsInStartDate = markupStartDate.match(/\d/g);
  if (!digitsInStartDate || digitsInStartDate.length < 8) {
    return null;
  }

  const filteredActiveParameters: FokParameterChoice[] = [];
  const filteredAvailableParameters: FokParameterInfo[] = [];
  let fokParameterValidity: { validFrom: string; validTo: string | null } | undefined = undefined;
  const parameterGroup = parameterGroups.find(paramGroup => isDateWithinValidityPeriod(markupStartDate, paramGroup));
  if (parameterGroup) {
    parameterGroup.fokParameterInfos.forEach(param => {
      const activeParam = activeParameters?.find(active => active.parameterValue === param.parameterValue);
      if (activeParam) {
        filteredActiveParameters.push(activeParam);
      } else {
        filteredAvailableParameters.push(param);
      }
    });
    fokParameterValidity = { validFrom: parameterGroup.validFrom, validTo: parameterGroup.validTo };
    return {
      activeParameters: filteredActiveParameters,
      availableParameters: filteredAvailableParameters,
      fokParameterValidity,
    };
  } else {
    return null;
  }
};
